import { CornerDownRight, PenLine, Users } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import { ApiError, type OrgChart as OrgChartData, type OrgChartLine, type OrgChartPerson } from "@/lib/api";
import { personColour, personInitials } from "@/lib/personAvatar";

/**
 * WORLD-10 — the org chart, under the People destination.
 *
 * A rendering over rows and nothing else: `reports_to` draws the reporting tree, `member_of`
 * and `leads` fill the team cards, and nothing about the layout is stored — no positions, no
 * order, no layout engine. A relation drawn on a dossier a second ago is on it, and a chart
 * with no rows is blank and says how it fills rather than guessing at a hierarchy.
 *
 * Two readings of the same people, because an organization is both at once: who reports to
 * whom (a tree, indented, one line per person), and which teams exist and who is on each (a
 * grid of cards). A person with more than one manager — an owner's line and a disputed one a
 * source proposed — is drawn under the first, which the Server orders owner-asserted first,
 * and says who else.
 */
export function OrgChart() {
  const { api } = useApp();
  const [chart, setChart] = useState<OrgChartData | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .getOrgChart()
      .then((read) => !cancelled && setChart(read))
      .catch((cause: unknown) => {
        if (!cancelled) setProblem(cause instanceof ApiError ? cause.message : "The org chart could not be read.");
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  if (problem) {
    return (
      <p role="alert" className="text-[length:var(--text-sm)] text-[var(--warning)]">
        {problem}
      </p>
    );
  }

  if (!chart) return <p className="text-[length:var(--text-sm)] text-[var(--text-secondary)]">Loading…</p>;

  return <OrgChartView chart={chart} />;
}

export function OrgChartView({ chart }: { chart: OrgChartData }) {
  const people = useMemo(() => new Map(chart.people.map((person) => [person.id, person])), [chart.people]);
  const teams = useMemo(() => new Map(chart.teams.map((team) => [team.id, team])), [chart.teams]);

  const reports = chart.lines.filter((line) => line.type === "reports_to" && people.has(line.subjectId) && people.has(line.objectId));
  const memberships = chart.lines.filter((line) => line.type === "member_of");
  const leads = chart.lines.filter((line) => line.type === "leads" && teams.has(line.objectId));

  // Each person's manager is the first reporting line (owner-asserted first); any further one is
  // shown as a note rather than drawing the person twice.
  const managerOf = new Map<string, OrgChartLine>();
  const alsoReportsTo = new Map<string, OrgChartLine[]>();
  for (const line of reports) {
    if (!managerOf.has(line.subjectId)) managerOf.set(line.subjectId, line);
    else alsoReportsTo.set(line.subjectId, [...(alsoReportsTo.get(line.subjectId) ?? []), line]);
  }

  const reportsOf = new Map<string, OrgChartLine[]>();
  for (const line of managerOf.values()) {
    reportsOf.set(line.objectId, [...(reportsOf.get(line.objectId) ?? []), line]);
  }

  const roots = chart.people.filter((person) => reportsOf.has(person.id) && !managerOf.has(person.id));

  const teamsOf = new Map<string, string[]>();
  for (const line of memberships) {
    if (teams.has(line.objectId)) teamsOf.set(line.subjectId, [...(teamsOf.get(line.subjectId) ?? []), line.objectId]);
  }

  // Placed means drawn somewhere on this page: a membership of an initiative is not.
  const placed = new Set<string>([
    ...reports.flatMap((line) => [line.subjectId, line.objectId]),
    ...memberships.filter((line) => teams.has(line.objectId)).map((line) => line.subjectId),
    ...leads.map((line) => line.subjectId),
  ]);
  const unplaced = chart.people.filter((person) => !placed.has(person.id));

  if (chart.lines.length === 0 && chart.teams.length === 0) {
    return (
      <div className="rounded-[var(--radius-xl)] border border-dashed border-[var(--border-default)] p-[var(--space-8)] text-center">
        <p className="text-[length:var(--text-sm)] text-[var(--text-primary)]">No org chart yet.</p>
        <p className="mx-auto mt-1 max-w-[56ch] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          It is drawn from who reports to whom and who is on which team. Add a team in Entities, then a
          reporting line or a membership on somebody's dossier, and it appears here — nothing is laid out by
          hand.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[var(--space-8)]">
      <section className="flex flex-col gap-[var(--space-3)]">
        <Heading title="Reporting lines" hint="Who reports to whom, from the reports-to relations." />
        {roots.length === 0 ? (
          <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
            No reporting lines yet. Add one on a person's dossier: “reports to”.
          </p>
        ) : (
          <ul className="flex flex-col gap-1">
            {roots.map((person) => (
              <TreeNode
                key={person.id}
                person={person}
                line={null}
                people={people}
                reportsOf={reportsOf}
                alsoReportsTo={alsoReportsTo}
                teamsOf={teamsOf}
                teamName={(id) => teams.get(id)?.name ?? ""}
                trail={new Set()}
              />
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-[var(--space-3)]">
        <Heading title="Teams" hint="Who leads each team and who is a member of it." />
        {chart.teams.length === 0 ? (
          <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
            No teams yet. A team is an entity of its own — add one in Entities.
          </p>
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-[var(--space-4)]">
            {chart.teams.map((team) => {
              const teamLeads = leads.filter((line) => line.objectId === team.id);
              const members = memberships.filter((line) => line.objectId === team.id && people.has(line.subjectId));
              const subTeams = memberships.filter((line) => line.objectId === team.id && teams.has(line.subjectId));
              const partOf = memberships.filter((line) => line.subjectId === team.id && teams.has(line.objectId));

              return (
                <div
                  key={team.id}
                  className="flex flex-col gap-3 rounded-[var(--radius-xl)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] p-[var(--space-4)]"
                >
                  <div className="flex items-start gap-2.5">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-lg)] border border-[var(--border-subtle)] bg-[var(--surface-2)] text-[var(--vl-virtual-soft)]">
                      <Users size={16} strokeWidth={2} aria-hidden />
                    </span>
                    <div className="min-w-0">
                      <Link
                        to={`/entities/${team.id}`}
                        className="font-bold text-[var(--text-primary)] hover:text-[var(--vl-life)]"
                      >
                        {team.name}
                      </Link>
                      {partOf.length > 0 && (
                        <div className="text-[length:var(--text-2xs)] text-[var(--text-muted)]">
                          in {partOf.map((line) => teams.get(line.objectId)?.name).join(", ")}
                        </div>
                      )}
                    </div>
                  </div>

                  {teamLeads.length > 0 && (
                    <Group label="Leads">
                      {teamLeads.map((line) =>
                        people.has(line.subjectId) ? (
                          <PersonChip key={line.id} person={people.get(line.subjectId)!} line={line} />
                        ) : null,
                      )}
                    </Group>
                  )}

                  <Group label={members.length === 1 ? "1 member" : `${members.length} members`}>
                    {members.length === 0 ? (
                      <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">Nobody yet.</span>
                    ) : (
                      members.map((line) => (
                        <PersonChip key={line.id} person={people.get(line.subjectId)!} line={line} />
                      ))
                    )}
                  </Group>

                  {subTeams.length > 0 && (
                    <Group label="Teams within">
                      {subTeams.map((line) => (
                        <Link
                          key={line.id}
                          to={`/entities/${line.subjectId}`}
                          className="rounded-full border border-[var(--border-subtle)] px-2.5 py-0.5 text-[length:var(--text-xs)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        >
                          {teams.get(line.subjectId)?.name}
                        </Link>
                      ))}
                    </Group>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {unplaced.length > 0 && (
        <section className="flex flex-col gap-[var(--space-3)]">
          <Heading
            title="Not placed yet"
            hint="No reporting line and no team. Open a dossier to add either."
          />
          <div className="flex flex-wrap gap-1.5">
            {unplaced.map((person) => (
              <PersonChip key={person.id} person={person} line={null} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function TreeNode({
  person,
  line,
  people,
  reportsOf,
  alsoReportsTo,
  teamsOf,
  teamName,
  trail,
}: {
  person: OrgChartPerson;
  line: OrgChartLine | null;
  people: Map<string, OrgChartPerson>;
  reportsOf: Map<string, OrgChartLine[]>;
  alsoReportsTo: Map<string, OrgChartLine[]>;
  teamsOf: Map<string, string[]>;
  teamName: (id: string) => string;
  /** The ancestors already drawn, so a loop the database refuses could still never hang this. */
  trail: Set<string>;
}) {
  const children = (reportsOf.get(person.id) ?? [])
    .filter((child) => !trail.has(child.subjectId))
    .sort((a, b) => (people.get(a.subjectId)?.name ?? "").localeCompare(people.get(b.subjectId)?.name ?? ""));
  const others = alsoReportsTo.get(person.id) ?? [];
  const next = new Set([...trail, person.id]);

  return (
    <li className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2.5 rounded-[var(--radius-md)] px-2 py-1.5 hover:bg-[var(--surface-glass)]">
        {line && <CornerDownRight size={12} strokeWidth={2} aria-hidden className="shrink-0 text-[var(--text-muted)]" />}
        <Avatar person={person} />
        <Link to={`/people/${person.id}`} className="font-semibold text-[var(--text-primary)] hover:text-[var(--vl-life)]">
          {person.name}
        </Link>
        {person.role && <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">{person.role}</span>}
        {(teamsOf.get(person.id) ?? []).map((team) => (
          <span
            key={team}
            className="rounded-full bg-[var(--secondary)] px-2 py-0.5 text-[length:var(--text-2xs)] text-[var(--secondary-foreground)]"
          >
            {teamName(team)}
          </span>
        ))}
        {line && <LineMarkers line={line} />}
        {others.length > 0 && (
          <span className="text-[length:var(--text-2xs)] text-[var(--warning)]">
            also reports to {others.map((other) => people.get(other.objectId)?.name).join(", ")}
          </span>
        )}
      </div>

      {children.length > 0 && (
        <ul className="ml-[18px] flex flex-col gap-1 border-l border-[var(--border-subtle)] pl-[var(--space-4)]">
          {children.map((child) => (
            <TreeNode
              key={child.id}
              person={people.get(child.subjectId)!}
              line={child}
              people={people}
              reportsOf={reportsOf}
              alsoReportsTo={alsoReportsTo}
              teamsOf={teamsOf}
              teamName={teamName}
              trail={next}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

function LineMarkers({ line }: { line: OrgChartLine }) {
  return (
    <>
      {line.assertedByUser && (
        <span title="Asserted by you" className="inline-flex text-[var(--vl-bridge-soft)]">
          <PenLine size={11} strokeWidth={2.5} aria-label="Asserted by you" />
        </span>
      )}
      {line.verdict === "challenge" && (
        <span className="rounded-full border border-[var(--destructive)] px-1.5 text-[length:var(--text-2xs)] font-semibold text-[var(--destructive)]">
          challenged
        </span>
      )}
    </>
  );
}

function PersonChip({ person, line }: { person: OrgChartPerson; line: OrgChartLine | null }) {
  return (
    <Link
      to={`/people/${person.id}`}
      className="inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-1)] py-0.5 pl-0.5 pr-2.5 text-[length:var(--text-xs)] text-[var(--text-primary)] hover:border-[var(--border-default)]"
    >
      <Avatar person={person} small />
      {person.name}
      {line && <LineMarkers line={line} />}
    </Link>
  );
}

function Avatar({ person, small = false }: { person: OrgChartPerson; small?: boolean }) {
  return (
    <span
      aria-hidden
      className={
        small
          ? "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-[var(--text-inverse)]"
          : "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[length:var(--text-2xs)] font-bold text-[var(--text-inverse)]"
      }
      style={{ background: personColour(person.id) }}
    >
      {personInitials(person.name)}
    </span>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[length:var(--text-2xs)] font-semibold uppercase tracking-[var(--tracking-wider)] text-[var(--text-muted)]">
        {label}
      </span>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function Heading({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-2.5">
      <h2 className="font-semibold text-[var(--text-primary)]">{title}</h2>
      <span className="text-[length:var(--text-xs)] text-[var(--text-muted)]">{hint}</span>
    </div>
  );
}
